import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext({
  user: null,
  loading: true,
  dataVersion: 0,
  setUser: () => {},
  logout: () => {},
  checkAuth: () => {},
  refreshData: () => {}
});

export const useAuth = () => useContext(AuthContext);

// Configurar axios para NUNCA cachear datos
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

// ---------- Session Token Bearer Fallback (bulletproof cross-origin login) ----------
// Stores session_token in localStorage and sends as Authorization: Bearer header.
// This avoids reliance on cookies/CORS/SameSite which may break across preview vs production domains.
export const SESSION_STORAGE_KEY = 'session_token';

export const setSessionToken = (token) => {
  if (token) {
    localStorage.setItem(SESSION_STORAGE_KEY, token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

export const clearSessionToken = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

// Apply saved token to axios on module load (before any request fires)
const _savedToken = typeof window !== 'undefined' ? localStorage.getItem(SESSION_STORAGE_KEY) : null;
if (_savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_savedToken}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);

  const clearAllCache = () => {
    // Limpiar localStorage (preservando tema y session token)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== 'theme' && key !== SESSION_STORAGE_KEY) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Limpiar sessionStorage
    sessionStorage.clear();
  };

  const refreshData = useCallback(() => {
    setDataVersion(prev => prev + 1);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // NOTE: No withCredentials — we rely on Authorization: Bearer <token> from localStorage.
      // This works cross-origin regardless of CORS/cookie/SameSite quirks.
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setUser(response.data);
      refreshData();
    } catch (error) {
      setUser(null);
      // If saved token is invalid, remove it
      if (error.response?.status === 401) {
        clearSessionToken();
      }
    } finally {
      setLoading(false);
    }
  }, [refreshData]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSessionToken();
      setUser(null);
      clearAllCache();
      refreshData();
    }
  };

  useEffect(() => {
    if (user) {
      clearAllCache();
    }
  }, [user?.user_id]);

  return (
    <AuthContext.Provider value={{ user, loading, dataVersion, setUser, logout, checkAuth, refreshData }}>
      {children}
    </AuthContext.Provider>
  );
};
